import React, { useEffect, useRef, useState } from 'react';
import { brandClass, brandLabel, BRAND_OPTIONS, LOCATION_LABELS, LOCATION_IDS, JAMES_LABELS, NOAH_LABELS, statusLabel, DEAL_STATUS_OPTIONS, dealClass, KEYS_OPTIONS, keysOption } from '../helpers';
import { BRAND_LOGO } from './Badges';
import { useSalespeople, withCurrent } from '../useSalespeople';

// ─────────────────────────────────────────────────────────────────────────
// DealSelect — SOLD/CUSTOMER/DEMO/STOCK/USED, styled as a solid pill
// ─────────────────────────────────────────────────────────────────────────
export function DealSelect({ value, onChange, disabled }) {
  const v = value || 'SOLD';
  if (disabled) {
    return <span className={`deal ${dealClass(v)}`}>{v}</span>;
  }
  return (
    <select
      className={`deal deal-edit ${dealClass(v)}`}
      value={v}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
    >
      {DEAL_STATUS_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
    </select>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// StatusSelect — native <select> styled as a status badge
// ─────────────────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { v: 'na',   label: 'N/A' },
  { v: 'ns',   label: 'Not Started' },
  { v: 'prog', label: 'In Progress' },
  { v: 'done', label: 'Complete' },
];

export function StatusSelect({ value, onChange, disabled }) {
  const v = value || 'na';
  // N/A reads as a quiet dash so real statuses stand out; still a select so
  // it can be flipped to Not Started etc. in one click.
  return (
    <select
      className={`sb sb-edit ${v}`}
      value={v}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      onClick={(e) => e.stopPropagation()}
      title={v === 'na' ? 'N/A — click to set' : statusLabel(v)}
    >
      {STATUS_OPTIONS.map((o) => (
        <option key={o.v} value={o.v}>{o.v === 'na' ? '—' : statusLabel(o.v)}</option>
      ))}
    </select>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// KeyIcon — where are the keys? Yellow = keys are here, grey = not here.
// Outline key with round head + jagged blade (matches the supplied artwork).
// ─────────────────────────────────────────────────────────────────────────
export function KeyIcon({ active, title }) {
  return (
    <svg
      width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke={active ? '#e0b000' : 'var(--border2)'}
      strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0, verticalAlign: 'middle' }}
      aria-label={title}
    >
      <title>{title}</title>
      <circle cx="12" cy="7" r="5" />
      <circle cx="12" cy="7" r="1.2" fill={active ? '#e0b000' : 'var(--border2)'} stroke="none" />
      <path d="M12 12v3l-2 1.5 2 2-2 2 2 2.5 2-2v-11" fill="none" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// SalespersonSelect — the list comes from the approved Sales accounts, so a
// new salesperson appears the moment their signup is approved.
// ─────────────────────────────────────────────────────────────────────────
export function SalespersonSelect({ value, onChange, disabled, className = '' }) {
  const names = withCurrent(useSalespeople(), value);
  return (
    <select
      className={className}
      value={value || ''}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      onClick={(e) => e.stopPropagation()}
      aria-label="Salesperson"
    >
      <option value="">— Salesperson —</option>
      {names.map((n) => <option key={n} value={n}>{n}</option>)}
    </select>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// KeysSelect — where the keys are. The key icon carries the state (grey =
// pending, yellow = ready) and the label carries the place, so the chip fits
// a pipeline cell. A native <select> can't hold an SVG, so the picker sits
// invisibly on top the same way BrandSelect does.
// ─────────────────────────────────────────────────────────────────────────
export function KeysSelect({ value, onChange, disabled }) {
  const opt = keysOption(value);
  return (
    <span className={`kcell ${opt.ready ? 'ready' : ''} ${disabled ? 'ro' : ''}`} title={opt.label}>
      <KeyIcon active={opt.ready} title={opt.label} />
      <span className="kcell-lbl">{opt.short}</span>
      <select
        className="kcell-pick"
        value={opt.id}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        onClick={(e) => e.stopPropagation()}
        aria-label="Where are the keys?"
      >
        {KEYS_OPTIONS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
      </select>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// LocationSelect
// ─────────────────────────────────────────────────────────────────────────
export function LocationSelect({ value, onChange, disabled }) {
  return (
    <select
      className={`lbg lbg-edit ${value}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      onClick={(e) => e.stopPropagation()}
    >
      {LOCATION_IDS.map((id) => (
        <option key={id} value={id}>{LOCATION_LABELS[id]}</option>
      ))}
    </select>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// BrandSelect
// ─────────────────────────────────────────────────────────────────────────
// Shows the brand logo (text chip for USED CARS / SERVICE); the picker sits
// invisibly on top so the cell stays editable with a single click.
export function BrandSelect({ value, onChange, disabled }) {
  const logo = BRAND_LOGO[value];
  const picker = (
    <select
      className="brand-pick"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      onClick={(e) => e.stopPropagation()}
      aria-label="Brand"
    >
      {BRAND_OPTIONS.map((b) => (
        <option key={b} value={b}>{brandLabel(b)}</option>
      ))}
    </select>
  );
  return (
    <span className={`brand-cell ${disabled ? 'ro' : ''}`}>
      {logo
        ? <img className="blogo" src={logo} alt={value} />
        : <span className={`btg ${brandClass(value)}`}>{brandLabel(value)}</span>}
      {picker}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// JNSelect — native <select> styled like the JN badge, 3 options visible
// ─────────────────────────────────────────────────────────────────────────
export function JNSelect({ value, type, onChange, disabled }) {
  const labels = type === 'james' ? JAMES_LABELS : NOAH_LABELS;
  const v = value || 0;
  // James: empty/j1/j2  ·  Noah: empty/n1/n2
  const cls = type === 'james'
    ? ['empty', 'j1', 'j2'][v]
    : ['empty', 'n1', 'n2'][v];

  if (disabled) {
    return <span className={`jnb ${cls}`}>{labels[v]}</span>;
  }
  return (
    <select
      className={`jnb jnb-edit ${cls}`}
      value={v}
      onChange={(e) => onChange(parseInt(e.target.value))}
      onClick={(e) => e.stopPropagation()}
    >
      {labels.map((label, i) => (
        <option key={i} value={i}>{label}</option>
      ))}
    </select>
  );
}

// Backwards-compat alias
export const JNCycler = JNSelect;

// ─────────────────────────────────────────────────────────────────────────
// TextEdit — display a value; click to edit inline; Enter/blur saves
// ─────────────────────────────────────────────────────────────────────────
export function TextEdit({ value, onChange, placeholder, displayClass = '', editStyle = {}, disabled }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(value || '');
  const inputRef              = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  if (disabled) {
    return (
      <span className={displayClass} style={{ color: value ? 'inherit' : 'var(--muted)' }}>
        {value || placeholder || '—'}
      </span>
    );
  }

  function commit() {
    setEditing(false);
    if (draft !== (value || '')) onChange(draft);
  }
  function cancel() {
    setEditing(false);
    setDraft(value || '');
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        className="cell-edit-input"
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { e.preventDefault(); cancel(); }
        }}
        style={editStyle}
      />
    );
  }

  return (
    <span
      className={`cell-edit-display ${displayClass}`}
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      title="Click to edit"
    >
      {value || <span style={{ color: 'var(--muted)' }}>{placeholder || '—'}</span>}
    </span>
  );
}
